/**
 * The app lock (mobile M4): fingerprint, face or the phone's PIN before
 * MyDeck shows a figure.
 *
 * The plan, with the owner's answers (2026-11-04):
 *
 *  * **On by default in the Android app**, with a switch in Settings → Devices
 *    to turn it off on this phone. (Compass made its lock opt-in; MyDeck holds
 *    every balance and transaction, and the plan asks for it on.)
 *  * **Asked on a cold start, and on returning after 5 minutes away.** A quick
 *    switch to the bank's app and back never asks.
 *  * **Never a trap.** It only locks a signed-in app (a sign-in screen has
 *    nothing to protect, and asking for a fingerprint right after a password
 *    would be absurd). Turning it *on* needs one successful check. A phone
 *    whose fingerprint and screen lock have since been removed opens unlocked
 *    rather than asking for something it can no longer do.
 *  * **A device setting**, kept in this phone's storage, not the account.
 *
 * Plain logic here, tested in a browser; the screen is `components/AppLock.tsx`.
 */

import { biometricAvailable, isNative, verifyOwner } from "@/platform";

const KEY = "mydeck.app-lock";

/** How long the app may be away before coming back asks again. */
export const RELOCK_AFTER_MS = 5 * 60_000;

/** Whether the lock is on for this phone. Off in every browser. */
export function appLockOn(): boolean {
  if (!isNative()) return false;
  try {
    return localStorage.getItem(KEY) !== "off";
  } catch {
    return true; // storage refused: keep the safer default
  }
}

function store(on: boolean): void {
  try {
    if (on) localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, "off");
  } catch {
    /* nothing to do: the default (on) stands */
  }
}

/** Turn the lock on after the person proves the check works on this phone. */
export async function enableAppLock(): Promise<boolean> {
  if (!(await verifyOwner("Confirm it's you to turn on the lock"))) return false;
  store(true);
  return true;
}

export function disableAppLock(): void {
  store(false);
}

/** Whether coming back after being hidden since `hiddenAt` should lock. */
export function relockDue(hiddenAt: number | null, now = Date.now()): boolean {
  return appLockOn() && hiddenAt !== null && now - hiddenAt >= RELOCK_AFTER_MS;
}

/** Ask for the check. True unlocks — including on a phone that can no longer
 *  check at all (see "never a trap"). */
export async function unlock(): Promise<boolean> {
  if (!(await biometricAvailable())) return true;
  return verifyOwner("Unlock MyDeck");
}

export const appLockAvailable = biometricAvailable;
